import React, { useEffect, useState } from "react";
import { TurnrModalContainer } from "~/components/modals/TurnrModal";
import { fetchRemoteComplaintImages } from "~/services";
import { RETURN_METHOD_COMPLAINT } from "~/constants";
import { canSelectProductExchangeToAlternatives, getProductComplaintReason } from "~/cores/productCore";
import { isRefundOptionSelected } from "~/cores/appCore";
import { serviceIsFetched, useServiceHook } from "rylak-common";
import ComplaintStepReason from "~/pages/[brand]/return/methodSelector/complaint/complaintSteps/ComplaintStepReason.js";
import ComplaintStepImageAndText from "~/pages/[brand]/return/methodSelector/complaint/complaintSteps/ComplaintStepImageAndText.js";
import ComplaintStepExchangeTo from "~/pages/[brand]/return/methodSelector/complaint/complaintSteps/ComplaintStepExchangeTo.js";
import ComplaintStepPaymentMethod from "~/pages/[brand]/return/methodSelector/complaint/complaintSteps/ComplaintStepPaymentMethod.js";
import ComplaintStepSubReason from "~/pages/[brand]/return/methodSelector/complaint/complaintSteps/ComplaintStepSubReason.js";
import { isReturnReasonMultipleChoice } from "backoffice/src/cores/returnReasonCore.js";

export function createComplaintMethodState({ product, complaintText = "", exchangeAlternativeId = null, complaintReasonId, complaintSubReasonId, complaintImageIds }) {
    return {
        productId: product?.sessionItemId,
        complaint: true,
        complaintText,
        exchangeAlternativeId,
        complaintReasonId,
        complaintSubReasonId,
        complaintImageIds,
        returnMethodType: RETURN_METHOD_COMPLAINT,
    };
}

const COMPLAINT_STEPS = {
    REASON: "REASON",
    SUB_REASON: "SUB_REASON",
    IMAGE_AND_TEXT: "IMAGE_AND_TEXT",
    EXCHANGE_TO: "EXCHANGE_TO",
    PAYMENT_METHOD: "PAYMENT_METHOD",
};

function ComplaintStepper(props) {
    const { appProps, backToMethodSelector, onModalClose, product, onSave, productExchangeServiceData } = props;

    const { sessionId } = appProps;

    const [selectedComplaintReasonId, setSelectedComplaintReasonId] = useState(null);
    const [selectedComplaintSubReasonId, setSelectedComplaintSubReasonId] = useState(null);
    const [activeComplaintStep, setActiveComplaintStep] = useState(COMPLAINT_STEPS.REASON);

    const isOnReasonStep = activeComplaintStep === COMPLAINT_STEPS.REASON;
    const isOnSubReasonStep = activeComplaintStep === COMPLAINT_STEPS.SUB_REASON;
    const isOnImageAndTextStep = activeComplaintStep === COMPLAINT_STEPS.IMAGE_AND_TEXT;
    const isOnExchangeToStep = activeComplaintStep === COMPLAINT_STEPS.EXCHANGE_TO;
    const isOnPaymentMethodStep = activeComplaintStep === COMPLAINT_STEPS.PAYMENT_METHOD;

    const canSelectedExchangeToAlternatives = canSelectProductExchangeToAlternatives(product, { appProps, productExchangeServiceData });

    const [complainTextInputValue, setComplainTextInputValue] = useState("");

    /** Upload image logic **/

    // We keep check on handled images to avoid re-adding an image we have removed locally when we poll for uploaded images
    const [handledImageIds, setHandledImageIds] = useState([]);
    const [selectedImages, setSelectedImages] = useState([]);

    // poll for remote/uploaded images, which may come from scanning the QR code using the phone
    const { serviceData: complaintImagesServiceData, fetchServiceData } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchData: { sessionId, productId: product.sessionItemId },
        fetchFn: fetchRemoteComplaintImages,
    });

    useEffect(() => {
        let intervalId;
        intervalId = setInterval(() => {
            // We need to poll since images might have been uploaded using the QR/phone
            fetchServiceData();
        }, 5000);
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, []);

    function onComplaintImageUpload() {
        // We reload images after each upload to get the latest successfully uploaded images
        fetchServiceData();
    }

    useEffect(() => {
        if (!serviceIsFetched(complaintImagesServiceData)) {
            return;
        }

        const remoteImages = complaintImagesServiceData?.data ?? [];
        remoteImages
            ?.reduce(async (initial, remoteImage) => {
                let result = await initial;
                if (handledImageIds.includes(remoteImage.id)) {
                    // This image has already been handled with by the user
                    return result;
                }
                const res = await fetch(remoteImage.downloadURL);
                const blob = await res.blob();
                if (!blob.type.startsWith("image/")) {
                    // We only accept blobs that are of type image. Otherwise something strange was uploaded
                    return result;
                }
                const sameSize = selectedImages.find((scd) => scd?.imageBlobFile?.size === blob.size);

                if (sameSize) {
                    // If the size is the exact the same as something we have already uploaded it is most likely the same image. We ignore it to avoid duplicates
                    return result;
                }

                result = result.concat({
                    id: remoteImage.id,
                    imageBlobFile: blob,
                });

                return result;
            }, Promise.resolve([]))
            .then((remoteImagesToAdd) => {
                if (remoteImagesToAdd?.length) {
                    setSelectedImages(selectedImages.concat(remoteImagesToAdd).slice(0, 5)); // We can never have more than 5 images
                    setHandledImageIds(handledImageIds.concat(remoteImagesToAdd.map((image) => image.id)));
                }
            });
    }, [selectedImages, handledImageIds, complaintImagesServiceData]);

    /** Saving and navigation logic **/

    function goToComplaintReasonStep() {
        setSelectedComplaintSubReasonId(null);
        setSelectedComplaintReasonId(null);
        setActiveComplaintStep(COMPLAINT_STEPS.REASON);
    }

    function goToComplaintSubReasonStep() {
        setSelectedComplaintSubReasonId(null);
        setActiveComplaintStep(COMPLAINT_STEPS.SUB_REASON);
    }

    function goToComplaintImageAndTextStep() {
        setActiveComplaintStep(COMPLAINT_STEPS.IMAGE_AND_TEXT);
    }

    function goToComplaintExchangeToStep() {
        setActiveComplaintStep(COMPLAINT_STEPS.EXCHANGE_TO);
    }

    function goToComplaintPaymentMethodStep() {
        setActiveComplaintStep(COMPLAINT_STEPS.PAYMENT_METHOD);
    }

    function onBack() {
        if (isOnReasonStep) {
            backToMethodSelector();
        }
        if (isOnSubReasonStep) {
            goToComplaintReasonStep();
        }
        if (isOnImageAndTextStep) {
            if (selectedComplaintSubReasonId) {
                goToComplaintSubReasonStep();
            } else {
                goToComplaintReasonStep();
            }
        }
        if (isOnExchangeToStep) {
            goToComplaintImageAndTextStep();
        }
        if (isOnPaymentMethodStep) {
            if (canSelectedExchangeToAlternatives) {
                goToComplaintExchangeToStep();
            } else {
                goToComplaintImageAndTextStep();
            }
        }
    }

    function complaintSave({ exchangeAlternativeId = null, refundOptionType } = {}) {
        const complaintMethodData = createComplaintMethodState({
            product,
            complaintText: complainTextInputValue,
            exchangeAlternativeId,
            complaintReasonId: selectedComplaintReasonId,
            complaintSubReasonId: selectedComplaintSubReasonId,
            complaintImageIds: selectedImages?.map((data) => data?.id),
        });
        onSave(complaintMethodData, refundOptionType);
    }

    function onComplaintReasonSelected(complaintReasonId) {
        setSelectedComplaintReasonId(complaintReasonId);
        const complaintReason = getProductComplaintReason(product, { complaintReasonId });
        if (isReturnReasonMultipleChoice({ returnReason: complaintReason })) {
            goToComplaintSubReasonStep();
        } else {
            goToComplaintImageAndTextStep();
            setComplainTextInputValue("");
        }
    }

    function onComplaintSubReasonSelected(complaintSubReasonId) {
        setSelectedComplaintSubReasonId(complaintSubReasonId);
        goToComplaintImageAndTextStep();
        setComplainTextInputValue("");
    }

    function onSaveImageAndText() {
        if (canSelectedExchangeToAlternatives) {
            goToComplaintExchangeToStep();
        } else {
            complaintSave();
        }
    }

    function onSaveExchangeTo(productVariantId) {
        if (!productVariantId && !isRefundOptionSelected(appProps)) {
            // No exchange to item selected and no refund option selected yet
            goToComplaintPaymentMethodStep();
        } else {
            // Since an exchange is selected. No check for payment method is needed
            complaintSave({ exchangeAlternativeId: productVariantId });
        }
    }

    function onPaymentMethodSave(refundOptionType) {
        complaintSave({ refundOptionType });
    }

    function onSelectedImageRemoved(imageId) {
        const newState = selectedImages.filter(({ id }) => id !== imageId);
        setSelectedImages(newState);
    }

    const complaintStepperProps = {
        // From method selector
        onModalClose,
        product,
        productExchangeServiceData,

        // From complaint stepper
        onBack,
        selectedComplaintReasonId,
        setSelectedComplaintReasonId,
        selectedComplaintSubReasonId,
        selectedImages,
        setSelectedImages,
        setHandledImageIds,
        onComplaintImageUpload,
        complainTextInputValue,
        setComplainTextInputValue,
        onSelectedImageRemoved,
        onComplaintReasonSelected,
        onComplaintSubReasonSelected,

        onSaveImageAndText,
        onSaveExchangeTo,
        onPaymentMethodSave,
    };

    return (
        <TurnrModalContainer>
            {isOnReasonStep && <ComplaintStepReason appProps={appProps} complaintStepperProps={complaintStepperProps} />}
            {isOnSubReasonStep && <ComplaintStepSubReason appProps={appProps} complaintStepperProps={complaintStepperProps} />}
            {isOnImageAndTextStep && <ComplaintStepImageAndText appProps={appProps} complaintStepperProps={complaintStepperProps} />}
            {isOnExchangeToStep && <ComplaintStepExchangeTo appProps={appProps} complaintStepperProps={complaintStepperProps} />}
            {isOnPaymentMethodStep && <ComplaintStepPaymentMethod appProps={appProps} complaintStepperProps={complaintStepperProps} />}
        </TurnrModalContainer>
    );
}

export default ComplaintStepper;
